<template>
  <default>
    <div class="cust-home">
      <dashboard-hero-carousel
        v-if="isAuthenticated"
        :greeting="`Hi, ${firstName}`"
      />

      <div class="cust-home-grid">
        <section class="cust-panel">
          <div class="cust-panel-head">
            <h2 class="cust-ui-heading">My Products</h2>
            <router-link :to="{ name: 'MyPolicies' }" class="cust-ui-link">View All</router-link>
          </div>
          <ul
            v-if="policiesLoading"
            class="cust-product-list cust-product-list--loading"
            aria-label="Loading your policies"
          >
            <li v-for="n in 3" :key="n">
              <div class="cust-product-row cust-product-skeleton-row">
                <div class="cust-skeleton cust-skeleton-icon"></div>

                <div class="cust-product-text">
                  <div class="cust-skeleton cust-skeleton-name"></div>
                  <div class="cust-skeleton cust-skeleton-meta"></div>
                </div>

                <div class="cust-skeleton cust-skeleton-chevron"></div>
              </div>
            </li>
          </ul>
          <div v-else-if="policyPreview.length === 0" class="cust-panel-empty">
            <v-icon color="#94a3b8" size="32">mdi-shield-off-outline</v-icon>
            <p>No active policies yet.</p>
            <router-link :to="{ name: 'CustomerProductSelect' }" class="cust-panel-cta">
              Browse products
            </router-link>
          </div>
          <ul v-else class="cust-product-list">
            <li v-for="policy in policyPreview" :key="policy.id">
              <router-link :to="policy.to" class="cust-product-row">
                <div class="cust-product-icon">
                  <v-icon color="#184679" size="22">{{ policy.icon }}</v-icon>
                </div>
                <div class="cust-product-text">
                  <span class="cust-product-name">{{ policy.title }}</span>
                  <span class="cust-product-meta">{{ policy.meta }}</span>
                </div>
                <v-icon size="20" color="#cbd5e1">mdi-chevron-right</v-icon>
              </router-link>
            </li>
          </ul>
        </section>

        <section class="cust-panel">
          <div class="cust-panel-head">
            <h2 class="cust-ui-heading">Quick Actions</h2>
            <router-link :to="{ name: 'CustomerProductSelect' }" class="cust-ui-link">Show More</router-link>
          </div>
          <div class="cust-quick-grid">
            <router-link
              v-for="action in quickActions"
              :key="action.label"
              :to="action.to"
              class="cust-quick-item"
            >
              <div class="cust-quick-icon">
                <v-icon color="white" size="22">{{ action.icon }}</v-icon>
              </div>
              <span>{{ action.label }}</span>
            </router-link>
          </div>
        </section>
      </div>

      <section class="cust-discover">
        <div class="cust-panel-head">
          <h2 class="cust-ui-heading">Discover Pacis products</h2>
          <router-link :to="{ name: 'CustomerProductSelect' }" class="cust-ui-link">Explore More</router-link>
        </div>
        <div class="cust-discover-grid">
          <component
            v-for="product in discoverProducts"
            :key="product.id"
            :is="product.live ? 'router-link' : 'div'"
            :to="product.live ? (product.route || { name: 'CustomerProductSelect' }) : undefined"
            class="cust-discover-card"
            :class="{ 'cust-discover-card--soon': product.soon }"
          >
            <div class="cust-discover-accent" :style="{ background: product.accent }" />
            <div class="cust-discover-body">
              <div
                class="cust-discover-icon"
                :style="{ background: discoverIconBg(product), color: product.accent }"
              >
                <v-icon :color="product.accent" size="24">{{ product.mdiIcon }}</v-icon>
              </div>
              <div class="cust-discover-text">
                <h3>{{ product.name }}</h3>
                <p>{{ product.description }}</p>
                <span v-if="product.soon" class="cust-discover-badge">Coming soon</span>
              </div>
              <span class="cust-discover-go" aria-hidden="true">
                <v-icon color="#184679" size="20">mdi-arrow-right</v-icon>
              </span>
            </div>
          </component>
        </div>
      </section>

      <section v-if="quotesLoading || quotesLength > 0" class="cust-panel tw-mt-6">
      <!-- <section v-if="true" class="cust-panel tw-mt-6"> -->
        <div class="cust-panel-head">
          <h2 class="cust-ui-heading">Recent quotes</h2>
          <router-link v-if="quotesLength > 5" :to="{ name: 'SavedQuotes' }" class="cust-ui-link">View all</router-link>
        </div>
        <quotes
          hide-when-empty
          :limit="5"
          @saved-quotes-loaded="quotesLoaded"
          @loading-change="quotesLoading = $event"
        />
      </section>

      <footer class="cust-home-footer">
        <div>
          <a href="https://www.pacisinsurance.com/terms" target="_blank" rel="noopener">Terms of Use</a>
          <a href="https://www.pacisinsurance.com/privacy" target="_blank" rel="noopener">Privacy Policy</a>
        </div>
        <span>&copy; {{ year }} Pacis Insurance</span>
      </footer>
    </div>
  </default>
</template>

<script>
import Default from '@/components/layout/Default';
import DashboardHeroCarousel from '@/components/dashboard/DashboardHeroCarousel';
import { CUSTOMER_PRODUCTS } from '@/constants/customerProducts';
import { mapGetters } from 'vuex';
import Quotes from '@/components/quotes/Quotes';

export default {
  components: {
    Quotes,
    Default,
    DashboardHeroCarousel,
  },
  data() {
    return {
      quotesLength: 0,
      quickActions: [
        { label: 'Buy Now', icon: 'mdi-shopping-outline', to: { name: 'CustomerProductSelect' } },
        { label: 'Get Quote', icon: 'mdi-file-document-outline', to: { name: 'CustomerProductSelect' } },
        { label: 'My Policies', icon: 'mdi-shield-check-outline', to: { name: 'MyPolicies' } },
        { label: 'Report Claim', icon: 'mdi-clipboard-alert-outline', to: { name: 'Claims' } },
        { label: 'Activity', icon: 'mdi-history', to: { name: 'SavedQuotes' } },
      ],
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser', 'isAuthenticated']),
    ...mapGetters('portalCache', ['customerPoliciesPreview', 'customerPoliciesLoading', 'customerQuotesLoading']),
    policyPreview() {
      return this.customerPoliciesPreview;
    },
    policiesLoading() {
      return this.customerPoliciesLoading;
    },
    quotesLoading() {
      return this.customerQuotesLoading;
    },
    firstName() {
      const name = this.authenticatedUser && this.authenticatedUser.name;
      if (name && name.trim()) {
        return name.trim().split(/\s+/)[0].toUpperCase();
      }
      const email = this.authenticatedUser && this.authenticatedUser.email;
      if (email) return email.split('@')[0].toUpperCase();
      return 'there';
    },
    discoverProducts() {
      return CUSTOMER_PRODUCTS.filter((p) => p.id !== 'sme').slice(0, 3);
    },
    year() {
      return new Date().getFullYear();
    },
  },
  mounted() {
    if (this.isAuthenticated) {
      this.$store.dispatch('portalCache/fetchCustomerPolicies', { force: true });
      this.$store.dispatch('portalCache/fetchCustomerQuotes');
    }
  },
  metaInfo: {
    title: 'Dashboard',
  },
  methods: {
    quotesLoaded(count) {
      if (typeof count === 'number') {
        this.quotesLength = count;
        return;
      }
      this.quotesLength = Array.isArray(count) ? count.length : 0;
    },
    discoverIconBg(product) {
      return `${product.accent}18`;
    },
  },
};
</script>

<style scoped>
.cust-home {
  width: 100%;
  padding-top: 8px;
}

.cust-home-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-bottom: 28px;
}

@media (min-width: 900px) {
  .cust-home-grid {
    grid-template-columns: 1.15fr 1fr;
  }
}

.cust-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px 22px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}

.cust-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.cust-panel-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.cust-panel-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 12px;
  text-align: center;
  color: #64748b;
  font-size: 0.88rem;
}

.cust-panel-cta {
  color: #184679;
  font-weight: 700;
  text-decoration: none;
}

.cust-product-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.cust-product-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 0;
  text-decoration: none;
  border-bottom: 1px solid #f1f5f9;
}

.cust-product-list li:last-child .cust-product-row {
  border-bottom: none;
  padding-bottom: 0;
}

.cust-product-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #eef4fb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cust-product-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cust-product-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #0f172a;
}

.cust-product-meta {
  font-size: 0.8125rem;
  color: #64748b;
  font-weight: 400;
}

.cust-quick-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px 10px;
}

.cust-quick-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: #334155;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
}

.cust-quick-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: #184679;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cust-discover {
  margin-bottom: 32px;
}

.cust-discover-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

@media (min-width: 900px) {
  .cust-discover-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.cust-discover-card {
  display: block;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  text-decoration: none;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}

.cust-discover-card:hover:not(.cust-discover-card--soon) {
  border-color: #cbd5e1;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.08);
  transform: translateY(-2px);
}

.cust-discover-card--soon {
  opacity: 0.92;
  cursor: default;
}

.cust-discover-accent {
  height: 4px;
  width: 100%;
}

.cust-discover-body {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 16px;
}

.cust-discover-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cust-discover-text {
  flex: 1;
  min-width: 0;
}

.cust-discover-text h3 {
  margin: 0 0 6px;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}

.cust-discover-text p {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #64748b;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cust-discover-badge {
  display: inline-block;
  margin-top: 8px;
  font-size: 0.6875rem;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  padding: 3px 8px;
  border-radius: 20px;
}

.cust-discover-go {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #eef4fb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 4px;
}

.cust-home-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  padding-top: 8px;
  font-size: 0.72rem;
  color: #94a3b8;
}

.cust-home-footer a {
  color: #64748b;
  text-decoration: none;
  margin-right: 16px;
}
/* =========================================
   Dashboard loading shimmer
   ========================================= */

.cust-skeleton {
  background: linear-gradient(
    90deg,
    #eeeeee 25%,
    #f7f7f7 50%,
    #eeeeee 75%
  );
  background-size: 200% 100%;
  animation: cust-shimmer 1.5s infinite;
  border-radius: 6px;
}

@keyframes cust-shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

.cust-product-skeleton-row {
  pointer-events: none;
  cursor: default;
}

.cust-skeleton-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  flex-shrink: 0;
}

.cust-skeleton-name {
  width: 55%;
  height: 14px;
}

.cust-skeleton-meta {
  width: 75%;
  height: 12px;
  margin-top: 6px;
}

.cust-skeleton-chevron {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
